import { useHistory, useParams } from "react-router";
import BaseCardLayout from "../../components/base-card-layout/base-card-layout";
import { Button } from '../../components/button/button';
import logoImage from "../../assets/images/symmio-logos/symmio-logo-text-white-side.png"
import { useEffect, useState } from "react";
import DeeplinkService from "../../utilities/services/deeplink-service";
import { DateTime } from "luxon";
import { Deeplink } from "../../models/interfaces/deeplink";
import { DeeplinkStatus } from "../../models/enumerations/deeplink-status";
import OrganizationService from "../../utilities/services/organization-service";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import errorMessageLogo from "../../assets/images/symmio-logos/symmio-logo-horizontal-dark.png";
import { useTranslation } from 'react-i18next';
import { LanguageSelectorUserMenu } from "../../components/language-selector-user-menu/language-selector-user-menu";
import AppContentUtil from "../../utilities/app-content-util";
import { ReactComponent as BehavioralHealthIcon } from "../../assets/icons/solid/behavioral-health.svg";
import { ReactComponent as MovementIcon } from "../../assets/icons/outline/movement.svg";
import { ReactComponent as PhysicalActivityIcon } from "../../assets/icons/solid/activity-level.svg";
import { ReactComponent as BodyCompIcon } from "../../assets/icons/solid/body-comp.svg";
import { ReactComponent as BreathingIcon } from "../../assets/icons/solid/breathing-qual.svg";
import { ReactComponent as InjuryHistoryIcon } from "../../assets/icons/solid/injury-history.svg";
import { ReactComponent as NutritionIcon } from "../../assets/icons/solid/nutrition-aware.svg";
import { ReactComponent as SleepIcon } from "../../assets/icons/solid/sleep-well.svg";
import videoReplacementImg from "../../assets/images/results-page-video-replacement.png"
import reportImg from "../../assets/images/landing/report.png"
import mobileAppImg from "../../assets/images/landing/mobile-app.png"
import movementImg from "../../assets/images/movement-assessment.png"
import lifestyleImg from "../../assets/images/lifestyle-assessment.png"
import { ReactComponent as WellnessScoreIcon } from "../../assets/icons/outline/icon_wellness-score.svg";
import { ReactComponent as FocusAreaIcon } from "../../assets/icons/outline/icon_magnifying-glass.svg";
import { ReactComponent as BarChartIcon } from "../../assets/icons/outline/nav-data.svg";
import { ReactComponent as ExerciseIcon } from "../../assets/icons/outline/icon_toe-touch.svg";
import { ReactComponent as AcademyIcon } from "../../assets/icons/nav/icon_academy.svg";
import { ReactComponent as ProgressIcon } from "../../assets/icons/outline/icon_progress.svg";
import { createLogger } from "../../utilities/logger";
import { ReactComponent as ClockIcon } from "../../assets/icons/outline/icon_clock.svg";
import { LeadLink } from "../../models/interfaces/lead-link";
import { SubscriptionLink } from "../../models/interfaces/subscription-link";

const log = createLogger("LandingPage");

export interface LandingPageProps {
    isLinkLoaded?: boolean;
    isLeadOrSub?: boolean;
    link?: LeadLink | SubscriptionLink | null;
    onButtonClick?: () => void;
}

interface LandingPageParams {
    id: string;
}

const COMPONENT_CLASS = "c-assessment-results";
const VIDEO_URL = "";

const LandingPage: React.FunctionComponent<LandingPageProps> = (props) => {
    const {
        isLeadOrSub,
        link,
        onButtonClick,
        isLinkLoaded,
    } = props;
    const { id } = useParams<LandingPageParams>();
    const history = useHistory();
    const { t } = useTranslation();
    const [deeplink, setDeeplink] = useState<Deeplink | null>(null);
    const [isLinkValid, setIsLinkValid] = useState(true);
    const [isLinkLoadedState, setIsLinkLoadedState] = useState(isLinkLoaded);
    const [linkErrorMessageHeader, setLinkErrorMessageHeader] = useState(t('pages.landingPage.errors.default_head'));
    const [linkErrorMessageBody, setLinkErrorMessageBody] = useState(t('pages.landingPage.errors.default_body'));
    const cards = [
        {
            hide: false,
            title: t('pages.landingPage.reviewWellnessReport'),
            body: t('pages.landingPage.reviewWellnessReport_desc'),
            img: reportImg,
            bullets: [
                {
                    icon: <WellnessScoreIcon />,
                    title: t('pages.landingPage.cards.wellness_title'),
                    body: t('pages.landingPage.cards.wellness_desc'),
                },
                {
                    icon: <FocusAreaIcon />,
                    title: t('pages.landingPage.cards.focus_title'),
                    body: t('pages.landingPage.cards.focus_desc'),
                },
                {
                    icon: <BarChartIcon color="#43B5F8" />,
                    title: t('pages.landingPage.cards.scores_title'),
                    body: t('pages.landingPage.cards.scores_desc'),
                },

            ],
        },
        {
            hide: isLeadOrSub || false,
            title: deeplink && deeplink.linkType === SymmioAccessType.AppLicense ? t('pages.landingPage.startYourWellnessProgram') : t('pages.landingPage.unlockPlan'),
            body: deeplink && deeplink.linkType === SymmioAccessType.AppLicense ? t('pages.landingPage.unlockPlan_desc') : t('pages.landingPage.unlockPlan'),
            img: mobileAppImg,
            bullets: [
                {
                    icon: <ExerciseIcon />,
                    title: t('pages.landingPage.cards.move_title'),
                    body: t('pages.landingPage.cards.move_desc'),
                },
                {
                    icon: <AcademyIcon color="#43B5F8" />,
                    title: t('pages.landingPage.cards.learn_title'),
                    body: t('pages.landingPage.cards.learn_desc'),
                },
                {
                    icon: <ProgressIcon />,
                    title: t('pages.landingPage.cards.track_title'),
                    body: t('pages.landingPage.cards.track_desc'),
                },
            ],
        },
    ];

    // this will only set to true when its a leadlink
    useEffect(() => {
        if (isLinkLoaded) {
            setIsLinkLoadedState(true);
        }
    }, [isLinkLoaded])

    useEffect(() => {
        const getDeeplink = async () => {
            let fetchedDeeplink: Deeplink | null = null;

            try {
                fetchedDeeplink = await DeeplinkService.get(id);
            } catch (err) {
                log.error("Cannot fetch the deeplink ID", {
                    exception: err,
                    deeplinkId: id,
                });
                setIsLinkValid(false);
            } finally {
                await checkDeeplinkValidity(fetchedDeeplink);
                setIsLinkLoadedState(true);
                setDeeplink(fetchedDeeplink);
            }
        }

        // If it doesn't exist, it means the link prop hasn't been passed,
        // so we're working with a Deeplink and not LeadLink or SubscriptionLink
        if (!isLeadOrSub) {
            getDeeplink();
        } else if (link) {
            checkLinkValidity(link);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, link]);

    const checkLinkValidity = async (linkToValidate: LeadLink | SubscriptionLink | null | undefined) => {
        const nowUtc = DateTime.now().toUTC();

        if (!linkToValidate) {
            setIsLinkValid(false);
            return;
        }

        const startDate = linkToValidate.startDate ? DateTime.fromISO(linkToValidate.startDate) : null;
        const endDate = linkToValidate.endDate ? DateTime.fromISO(linkToValidate.endDate) : null;

        const errorFutureHeader = t('pages.landingPage.errors.future_head');
        const errorFutureBody = t('pages.landingPage.errors.future_body', { startDate: startDate?.toLocaleString(DateTime.DATE_FULL) });

        const errorExpiredHeader = t('pages.landingPage.errors.expired_head');
        const errorExpiredBody = t('pages.landingPage.errors.expired_body');

        const errorInactiveHeader = t('pages.landingPage.errors.inactive_head');
        const errorInactiveBody = t('pages.landingPage.errors.inactive_body');

        // If start date is a future date
        if (startDate && nowUtc <= startDate.toUTC().startOf("day")) {
            setLinkErrorMessageHeader(errorFutureHeader);
            setLinkErrorMessageBody(errorFutureBody);
            setIsLinkValid(false);
            return;
        }

        // If end date has passed
        else if (endDate && nowUtc >= endDate.toUTC().endOf("day")) {
            setLinkErrorMessageHeader(errorExpiredHeader);
            setLinkErrorMessageBody(errorExpiredBody);
            setIsLinkValid(false);
            return;
        }

        // If the deeplink is not active
        else if (linkToValidate.status !== DeeplinkStatus.Active) {
            setLinkErrorMessageHeader(errorInactiveHeader);
            setLinkErrorMessageBody(errorInactiveBody);
            setIsLinkValid(false);
            return;
        }

        setIsLinkValid(true);
    }

    const checkDeeplinkValidity = async (deeplink: Deeplink | null) => {
        const nowUtc = DateTime.now().toUTC();

        if (!deeplink) {
            setIsLinkValid(false);
            return;
        }

        const startDate = deeplink.startDate ? DateTime.fromISO(deeplink.startDate) : null;
        const endDate = deeplink.endDate ? DateTime.fromISO(deeplink.endDate) : null;

        const errorFutureHeader = t('pages.landingPage.errors.future_head');
        const errorFutureBody = t('pages.landingPage.errors.future_body', { startDate: startDate?.toLocaleString(DateTime.DATE_FULL) });

        const errorExpiredHeader = t('pages.landingPage.errors.expired_head');
        const errorExpiredBody = t('pages.landingPage.errors.expired_body');

        const errorInactiveHeader = t('pages.landingPage.errors.inactive_head');
        const errorInactiveBody = t('pages.landingPage.errors.inactive_body');

        const errorLimitHeader = t('pages.landingPage.errors.limit_head');
        const errorLimitBody = t('pages.landingPage.errors.limit_body');

        const errorOrganizationLimitHeader = t('pages.landingPage.errors.limit_head');
        const errorOrganizationLimitBody = t('pages.landingPage.errors.orgLimit_body');

        const errorLicenseLimitHeader = t('pages.landingPage.errors.limit_head');
        const errorLicenseLimitBody = t('pages.landingPage.errors.licenseLimit_body');


        // If start date is a future date
        if (startDate && nowUtc <= startDate.toUTC().startOf("day")) {
            setLinkErrorMessageHeader(errorFutureHeader);
            setLinkErrorMessageBody(errorFutureBody);
            setIsLinkValid(false);
            return;
        }

        // If end date has passed
        else if (endDate && nowUtc >= endDate.toUTC().endOf("day")) {
            setLinkErrorMessageHeader(errorExpiredHeader);
            setLinkErrorMessageBody(errorExpiredBody);
            setIsLinkValid(false);
            return;
        }

        // If the deeplink is not active
        else if (deeplink.status !== DeeplinkStatus.Active) {
            setLinkErrorMessageHeader(errorInactiveHeader);
            setLinkErrorMessageBody(errorInactiveBody);
            setIsLinkValid(false);
            return;
        }

        // If the link has reached its usage limit
        else if (deeplink.used && deeplink.limit && deeplink.used >= deeplink.limit) {
            setLinkErrorMessageHeader(errorLimitHeader);
            setLinkErrorMessageBody(errorLimitBody);
            setIsLinkValid(false);
            return;
        }

        else if (deeplink.organizationId) {
            const org = await OrganizationService.get(deeplink.organizationId);

            // If the organization has reached its user limit
            if (org && org.userLimit && org.userCount && org.userCount >= org.userLimit) {
                setLinkErrorMessageHeader(errorOrganizationLimitHeader);
                setLinkErrorMessageBody(errorOrganizationLimitBody);
                setIsLinkValid(false);
                return;
            }

            // If the organization has reached its license limit
            else if (deeplink.linkType === SymmioAccessType.AppLicense && org && org.mySymmioLicense && org.mySymmioLicenseCount && org.mySymmioLicenseCount >= org.mySymmioLicense) {
                setLinkErrorMessageHeader(errorLicenseLimitHeader);
                setLinkErrorMessageBody(errorLicenseLimitBody);
                setIsLinkValid(false);
                return;
            }
        }

        setIsLinkValid(true);
    }

    const errorMessage = (
        <BaseCardLayout>
            <div className={`${COMPONENT_CLASS}__error`}>
                <div className={`${COMPONENT_CLASS}__error-content-container`}>
                    <div className={`${COMPONENT_CLASS}__error-image-container`}>
                        <img src={errorMessageLogo} alt="Symmio Logo" />
                    </div>

                    <div className={`${COMPONENT_CLASS}__error-message-container`}>
                        <h1>{linkErrorMessageHeader}</h1>
                        <p className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: linkErrorMessageBody }}></p>
                    </div>
                </div>
            </div>
        </BaseCardLayout>
    );
    // TODO: sometimes link isn't loaded and even though they are lead link, they go down regular flow.
    // need to ensure link is always loaded for lead links
    const assessmentButton = (isBlue: boolean = false) => (
        <Button
            type="default"
            buttonText={t('buttons.btn_startAssessment')}
            buttonStyle={isBlue ? "blue" : undefined}
            disabled={!isLinkLoadedState}
            onClick={() => {
                // Lead Capture or Subscription Link
                if (link && onButtonClick) {
                    onButtonClick();
                }
                // Deeplink
                else {
                    history.push(`/link-account-creation/${id}`)
                }
            }} />
    );

    return (
        <>
            <div className="user-menu">
                <LanguageSelectorUserMenu userPortal={false} />
            </div>
            <div className={`${COMPONENT_CLASS}`}>
                {isLinkValid
                    ?
                    <BaseCardLayout>
                        <div className="w-full">
                            <div className="bg-black-gray text-white py-16 px-4 md:px-16" style={{ borderRadius: "40px 40px 0 0" }}>
                                <div>
                                    <img src={logoImage} alt="Symmio Logo" />
                                </div>
                                <div className="flex flex-wrap justify-between mt-4 items-center gap-8">
                                    <div className={`${COMPONENT_CLASS}__header-companion`}>
                                        <h1 className="text-white">{t('pages.landingPage.title')}</h1>
                                        <p className="mb-4">
                                            {t('pages.landingPage.subtitle')}
                                        </p>
                                        <div className="flex justify-center md:justify-start">
                                            {assessmentButton(true)}
                                        </div>
                                        <div className="text-xs font-light flex gap-2 mt-4">
                                            <span>
                                                <ClockIcon color="#FFF" />
                                            </span>
                                            <span>
                                                {t('pages.landingPage.time')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={`${COMPONENT_CLASS}__video-companion`}>
                                        {VIDEO_URL
                                            ?
                                            <div className={`${COMPONENT_CLASS}__video-wrapper`} dangerouslySetInnerHTML={{ __html: AppContentUtil.getVideoEmbedCode(VIDEO_URL) }}></div>
                                            :
                                            <img src={videoReplacementImg} alt="Symmio Mobile App" />
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="py-16">
                                <div className="px-4 md:px-16">
                                    <h2>{t('assessmentResults.startYourJourney')}</h2>
                                    <p className="text-center py-6">
                                        {t('assessmentResults.startYourJourney_desc')}
                                    </p>
                                </div>

                                <div className="flex flex-wrap justify-center md:justify-between gap-6 px-4 md:px-16">
                                    <div className={`${COMPONENT_CLASS}__focus-area-vertical`}>
                                        <div style={{ backgroundColor: "#ADDFFD" }}>
                                            <span>
                                                <MovementIcon style={{ backgroundColor: "#ADDFFD" }} />
                                            </span>
                                        </div>
                                        <span dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.movementHealth') }}></span>
                                    </div>

                                    <div className={`${COMPONENT_CLASS}__focus-area-vertical`}>
                                        <div style={{ backgroundColor: "#8BE3CE" }}>
                                            <span>
                                                <BehavioralHealthIcon style={{ backgroundColor: "#8BE3CE" }} />
                                            </span>
                                        </div>
                                        <span dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.behavioralHealth') }}></span>
                                    </div>

                                    <div className={`${COMPONENT_CLASS}__focus-area-vertical`}>
                                        <div style={{ backgroundColor: "#FFA998" }}>
                                            <span>
                                                <InjuryHistoryIcon style={{ backgroundColor: "#FFA998" }} />
                                            </span>
                                        </div>
                                        <span dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.injuryHistory') }}></span>
                                    </div>

                                    <div className={`${COMPONENT_CLASS}__focus-area-vertical`}>
                                        <div style={{ backgroundColor: "#ADDFFD" }}>
                                            <span>
                                                <BreathingIcon style={{ backgroundColor: "#ADDFFD" }} />
                                            </span>
                                        </div>
                                        <span dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.breathingQuality') }}></span>
                                    </div>

                                    <div className={`${COMPONENT_CLASS}__focus-area-vertical`} >
                                        <div style={{ backgroundColor: "#FFA998" }}>
                                            <span>
                                                <PhysicalActivityIcon style={{ backgroundColor: "#FFA998" }} />
                                            </span>
                                        </div>
                                        <span dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.physicalActivity') }}></span>
                                    </div>

                                    <div className={`${COMPONENT_CLASS}__focus-area-vertical`}>
                                        <div style={{ backgroundColor: "#FFC79C" }}>
                                            <span>
                                                <BodyCompIcon style={{ backgroundColor: "#FFC79C" }} />
                                            </span>
                                        </div>
                                        <span dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.bodyComposition') }}></span>
                                    </div>

                                    <div className={`${COMPONENT_CLASS}__focus-area-vertical`}>
                                        <div style={{ backgroundColor: "#CEBAF8" }}>
                                            <span>
                                                <SleepIcon style={{ backgroundColor: "#CEBAF8" }} />
                                            </span>
                                        </div>
                                        <span dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.sleepWellness') }}></span>
                                    </div>

                                    <div className={`${COMPONENT_CLASS}__focus-area-vertical`} >
                                        <div style={{ backgroundColor: "#8BE3CE" }}>
                                            <span>
                                                <NutritionIcon style={{ backgroundColor: "#8BE3CE" }} />
                                            </span>
                                        </div>
                                        <span dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.nutritionalAwareness') }}></span>
                                    </div>
                                </div>

                                <div className="py-16 px-4 md:px-16">
                                    <h2>{t('assessmentResults.howItWorks')}</h2>
                                    <div className="flex flex-wrap gap-6 mt-6 justify-center">
                                        <div className={`${COMPONENT_CLASS}__step`}>
                                            <div className="flex justify-between items-center">
                                                <div className={`${COMPONENT_CLASS}__card-number`}>1</div>
                                            </div>
                                            <div>
                                                <h3 className="mt-4 mb-2">{t('pages.landingPage.step1_title')}</h3>
                                                <p>
                                                    {t('pages.landingPage.step1_desc')}
                                                </p>
                                            </div>
                                        </div>

                                        <div className={`${COMPONENT_CLASS}__step`}>
                                            <div className="flex justify-between items-center">
                                                <div className={`${COMPONENT_CLASS}__card-number`}>2</div>
                                            </div>
                                            <div>
                                                <h3 className="mt-4 mb-2">{t('pages.landingPage.step2_title')}</h3>
                                                <p>
                                                    {t('pages.landingPage.step2_desc')}
                                                </p>
                                            </div>
                                        </div>

                                        <div className={`${COMPONENT_CLASS}__step`}>
                                            <div className="flex justify-between items-center">
                                                <div className={`${COMPONENT_CLASS}__card-number`}>3</div>
                                            </div>
                                            <div>
                                                <h3 className="mt-4 mb-2">{t('pages.landingPage.step3_title')}</h3>
                                                <p>
                                                    {t('pages.landingPage.step3_desc')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="py-10 px-4 md:px-16 flex flex-col gap-4 items-center justify-center text-center bg-blue-light">
                                    <h2>{t('pages.landingPage.getStartedAssessment')}</h2>
                                    <div className="flex gap-4 justify-center w-full flex-wrap">
                                        <div style={{ width: "300px" }}>
                                            <img src={movementImg} alt="Movement Assessment" />
                                        </div>
                                        <div style={{ width: "300px" }}>
                                            <img src={lifestyleImg} alt="Lifestyle Assessment" />
                                        </div>
                                    </div>
                                    <div>{assessmentButton(false)}</div>
                                </div>

                                <div className="pt-16 px-4 md:px-16">
                                    <div>
                                        {cards.map((card, index) => {
                                            let flexDirection = "flex-row-reverse";

                                            if (index % 2 === 0) {
                                                flexDirection = "flex-row";
                                            }

                                            if (card.hide) {
                                                return <></>;
                                            }

                                            return (
                                                <>
                                                    <h2>{card.title}</h2>
                                                    <p className="mt-2 mb-6 text-center">
                                                        {card.body}
                                                    </p>
                                                    <div className={`flex flex-wrap items-center gap-6 mb-16 ${flexDirection}`}>
                                                        <div className={`${COMPONENT_CLASS}__card-image-wrapper`}>
                                                            <img className={`${COMPONENT_CLASS}__card-image__380`} src={card.img} alt="" />
                                                        </div>
                                                        <div className="flex flex-col gap-6 flex-1">
                                                            <div className="flex gap-5">
                                                                <div className="flex flex-col gap-6 flex-1">
                                                                    {card.bullets.map((bullet, index) => {
                                                                        return (
                                                                            <div className="flex gap-5">
                                                                                <div style={{ minWidth: "2rem", maxWidth: "2rem" }}>
                                                                                    {bullet.icon}
                                                                                </div>
                                                                                <div className="flex flex-col">
                                                                                    <h3 className="mb-1">{bullet.title}</h3>
                                                                                    <p>
                                                                                        {bullet.body}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="py-16 px-4 md:px-16 bg-blue-light flex flex-col gap-10 justify-center items-center" style={{ borderRadius: "0 0 40px 40px" }}>
                                <h2>{t('pages.landingPage.discover')}</h2>
                                <div>
                                    {assessmentButton(false)}
                                </div>
                            </div>
                        </div >
                    </BaseCardLayout >
                    :
                    errorMessage
                }
            </div >
        </>
    );
};

export default LandingPage;
